import { type PropsWithChildren } from 'react';

import { defaultThreshold } from '../constants.js';
import {
  defaultConfig,
  TimeseriesChartAnnotationsConfigContext,
} from '../context/TimeseriesChartAnnotationsConfig.context.js';
import { TimeseriesChartSelectContext } from '../context/TimeseriesChartSelect.context.js';
import { TimeseriesChartSeriesCurve } from '../context/TimeseriesChartSeriesCurve.context.js';
import { TimeseriesChartValueRepresentation } from '../context/TimeseriesChartValueRepresentation.context.js';
import { TimeseriesColorPaletteContext } from '../context/TimeseriesColorPalette.context.js';
import { TimeseriesGapPolicyContext } from '../context/TimeseriesGapPolicy.context.js';
import { TimeseriesInfiniteZoomContext } from '../context/TimeseriesInfiniteZoom.context.js';
import { TimeseriesInteractionsContext } from '../context/TimeseriesInteractions.context.js';
import { TimeseriesLegendContext } from '../context/TimeseriesLegend.context.js';
import { TimeseriesPointsDisplayContext } from '../context/TimeseriesPointsDisplayContext.js';
import { TimeseriesThresholdContext } from '../context/TimeseriesThreshold.context.js';
import { TimeseriesToolbarContext } from '../context/TimeseriesToolbar.context.js';
import { TimeseriesTooltipContext } from '../context/TimeseriesTooltip.context.js';
import { TimeseriesTruncationModeContext } from '../context/TimeseriesTruncationMode.context.js';
import { TimeseriesXAxisConfigContext } from '../context/TimeseriesXAxisConfig.context.js';
import { TimeseriesYAxisContext } from '../context/TimeseriesYAxis.context.js';
import { TimeseriesZoomRangesContext } from '../context/TimeseriesZoomRanges.context.js';
import { TimeseriesChartConfiguration } from '../types/timeseries-config.js';

export const TimeseriesConfigProvider = ({
  children,
  config,
}: PropsWithChildren<{
  config: TimeseriesChartConfiguration;
}>) => (
  <TimeseriesColorPaletteContext.Provider value={config.colorPalette}>
    <TimeseriesGapPolicyContext.Provider value={config.gapPolicy}>
      <TimeseriesToolbarContext.Provider value={config.toolbar}>
        <TimeseriesInteractionsContext.Provider value={config.interactions}>
          <TimeseriesInfiniteZoomContext.Provider value={config.infiniteZoom}>
            <TimeseriesZoomRangesContext.Provider
              value={{
                initialZoom: config.initialZoom,
                currentZoom: config.currentZoom,
              }}
            >
              <TimeseriesTruncationModeContext.Provider
                value={config.truncationMode}
              >
                <TimeseriesLegendContext.Provider value={config.legend}>
                  <TimeseriesPointsDisplayContext.Provider
                    value={config.pointsDisplay}
                  >
                    <TimeseriesTooltipContext.Provider value={config.tooltip}>
                      <TimeseriesXAxisConfigContext.Provider
                        value={config.xAxis}
                      >
                        <TimeseriesYAxisContext.Provider value={config.yAxis}>
                          <TimeseriesChartAnnotationsConfigContext.Provider
                            value={config.annotations || defaultConfig}
                          >
                            <TimeseriesThresholdContext.Provider
                              value={config.thresholds ?? defaultThreshold}
                            >
                              <TimeseriesChartSeriesCurve.Provider
                                value={config.curve}
                              >
                                <TimeseriesChartValueRepresentation.Provider
                                  value={config.valueRepresentation}
                                >
                                  <TimeseriesChartSelectContext.Provider
                                    value={config.select}
                                  >
                                    {children}
                                  </TimeseriesChartSelectContext.Provider>
                                </TimeseriesChartValueRepresentation.Provider>
                              </TimeseriesChartSeriesCurve.Provider>
                            </TimeseriesThresholdContext.Provider>
                          </TimeseriesChartAnnotationsConfigContext.Provider>
                        </TimeseriesYAxisContext.Provider>
                      </TimeseriesXAxisConfigContext.Provider>
                    </TimeseriesTooltipContext.Provider>
                  </TimeseriesPointsDisplayContext.Provider>
                </TimeseriesLegendContext.Provider>
              </TimeseriesTruncationModeContext.Provider>
            </TimeseriesZoomRangesContext.Provider>
          </TimeseriesInfiniteZoomContext.Provider>
        </TimeseriesInteractionsContext.Provider>
      </TimeseriesToolbarContext.Provider>
    </TimeseriesGapPolicyContext.Provider>
  </TimeseriesColorPaletteContext.Provider>
);
